import { useEffect, useState } from "react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Account = {
  id: string;
  email: string;
  status: string;
  browserbaseContextId: string | null;
  lastLoginAt: string | null;
  lastHealthCheckAt: string | null;
  contextStatus: "none" | "pending" | "active" | "invalidated";
  createdAt: string;
};

type BootstrapSession = {
  sessionId: string;
  contextId: string;
  liveViewUrl: string;
  probeAsin: string | null;
};

function AdminRetailAccountsInner() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bootstrapFor, setBootstrapFor] = useState<{
    accountId: string;
    session: BootstrapSession;
  } | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  async function refresh() {
    const res = await api<{ accounts: Account[] }>("/admin/retail-accounts");
    if (res.ok && res.data) setAccounts(res.data.accounts);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setToast(null);
    setCreating(true);
    const res = await api("/admin/retail-accounts", {
      method: "POST",
      body: JSON.stringify({ email: newEmail.trim() }),
    });
    setCreating(false);
    if (!res.ok) {
      setError(res.error ?? "Could not register account.");
      return;
    }
    setNewEmail("");
    setToast("Account registered. Click \"Log in\" to connect it to Amazon.");
    void refresh();
  }

  async function bootstrap(a: Account) {
    setError(null);
    setToast(null);
    setWorking(a.id);
    const res = await api<BootstrapSession>(
      `/admin/retail-accounts/${a.id}/bootstrap`,
      { method: "POST" }
    );
    setWorking(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Bootstrap failed.");
      return;
    }
    setBootstrapFor({ accountId: a.id, session: res.data });
  }

  async function confirm() {
    if (!bootstrapFor) return;
    setError(null);
    setToast(null);
    setWorking(bootstrapFor.accountId);
    const res = await api<{
      loggedIn: boolean;
      detectedName: string | null;
      reviewAccessGated?: boolean;
    }>(
      `/admin/retail-accounts/${bootstrapFor.accountId}/confirm`,
      {
        method: "POST",
        body: JSON.stringify({
          bootstrapSessionId: bootstrapFor.session.sessionId,
        }),
      }
    );
    setWorking(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Confirm failed.");
      return;
    }
    if (res.data.reviewAccessGated) {
      setError(
        "Amazon is still limiting review access for this account. Wait for the access request to be approved, then try logging in again."
      );
      setBootstrapFor(null);
      void refresh();
      return;
    }
    if (!res.data.loggedIn) {
      setError(
        "Not logged in — Amazon still shows the sign-in page. Try again."
      );
      return;
    }
    setToast(
      `Logged in${res.data.detectedName ? ` as ${res.data.detectedName}` : ""}. Context saved.`
    );
    setBootstrapFor(null);
    void refresh();
  }

  return (
    <AdminLayout>
      <h1 className="text-xl font-semibold tracking-tight">Retail Amazon accounts</h1>
      <p className="mt-1 text-sm text-slate-600">
        Authenticated Amazon retail accounts used to scrape <code>/product-reviews/</code>{" "}
        pages (with star filters + pagination). The public scraper sees only the
        top-reviews block, so verification flows need this auth path.
      </p>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold">Register an account</h2>
        <p className="mt-1 text-xs text-slate-600">
          Use an Amazon customer (retail) account, not a Seller Central
          account. <code>agent@reputely.co</code> is fine if it has retail
          login set up.
        </p>
        <form onSubmit={create} className="mt-3 flex flex-wrap gap-3">
          <input
            type="email"
            required
            placeholder="retail@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="flex-1 min-w-[260px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <Button type="submit" disabled={creating} size="sm">
            {creating ? "Adding…" : "Register account"}
          </Button>
        </form>
        {error && <p className="mt-2 text-xs text-red-600" role="alert">{error}</p>}
        {toast && (
          <p className="mt-2 text-xs text-emerald-700" role="status">
            {toast}
          </p>
        )}
      </section>

      {bootstrapFor && (
        <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-sm font-semibold text-amber-900">
            Log in to Amazon
          </h2>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-amber-900">
            <li>
              Open the browser link below in a new tab. It starts on the same
              Amazon review surface the scraper uses.
            </li>
            <li>
              Log in with the retail account credentials, including any
              password, captcha, MFA, or "is this you?" prompts.
            </li>
            <li>
              When the review page loads without a sign-in wall, come back here
              and click <strong>I'm logged in</strong>.
            </li>
          </ol>
          <div className="mt-3">
            <a
              href={bootstrapFor.session.liveViewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800"
            >
              Open Amazon login →
            </a>
          </div>
          <p className="mt-3 break-all text-[11px] text-slate-500">
            {bootstrapFor.session.liveViewUrl}
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={confirm}
              disabled={working === bootstrapFor.accountId}
            >
              {working === bootstrapFor.accountId ? "Checking…" : "I'm logged in"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setBootstrapFor(null)}
            >
              Cancel
            </Button>
          </div>
        </section>
      )}

      <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Context</th>
              <th className="px-4 py-2 text-left">Last login</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && accounts.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No retail accounts yet.</td></tr>
            )}
            {accounts.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-3 font-mono text-xs">{a.email}</td>
                <td className="px-4 py-3">
                  {a.status === "review_access_gated"
                    ? "Review access gated"
                    : a.status}
                </td>
                <td className="px-4 py-3">
                  <ContextBadge status={a.contextStatus} />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {a.lastLoginAt
                    ? new Date(a.lastLoginAt).toLocaleString()
                    : "never"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    type="button"
                    size="sm"
                    variant={a.contextStatus === "active" ? "outline" : "default"}
                    onClick={() => bootstrap(a)}
                    disabled={working === a.id || Boolean(bootstrapFor)}
                  >
                    {a.contextStatus === "active" ? "Re-log in" : "Log in"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </AdminLayout>
  );
}

export default function AdminRetailAccounts() {
  return (
    <AdminGate>
      <AdminRetailAccountsInner />
    </AdminGate>
  );
}

function ContextBadge({ status }: { status: Account["contextStatus"] }) {
  const cls = {
    none: "bg-slate-100 text-slate-700",
    pending: "bg-amber-50 text-amber-700",
    active: "bg-emerald-50 text-emerald-700",
    invalidated: "bg-red-50 text-red-700",
  }[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}
